import React, { useState, useEffect, useMemo } from "react";
import Authentication from "../../api/Authentication/Authentication";
import API from "../../api/API";

const AdminVisitors = () => {
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [aggregatedTable, setAggregatedTable] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch current user and IP addresses on mount
  useEffect(() => {
    const user = Authentication.getCurrentUser();

    if (user) {
      API.getIpAddress(user.token, {}, {$natural:-1}, 500)
        .then((result) => {
          if (result.data.success) {
            setIps(result.data.info);
          } else {
            setError("Failed to load IP addresses.");
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching IP addresses:", err);
          setError("An error occurred while fetching IP addresses.");
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Aggregate IP addresses so duplicates are merged and the latest visit is kept.
  const aggregatedIps = useMemo(() => {
    const aggregatedArray = [];
    ips.forEach((ip) => {
      const index = aggregatedArray.findIndex((item) => item.ip === ip.ip);
      if (index !== -1) {
        if (aggregatedArray[index].last_visited < ip.last_visited) {
          aggregatedArray[index].last_visited = ip.last_visited;
        }
      } else {
        aggregatedArray.push(ip);
      }
    });
    return aggregatedArray;
  }, [ips]);

  // Filter the data based on the search query and view mode.
  const filteredData = useMemo(() => {
    const dataToFilter = aggregatedTable ? aggregatedIps : ips;
    return dataToFilter.filter(
      (ip) =>
        ip.ip.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ip.location.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [aggregatedTable, aggregatedIps, ips, searchQuery]);

  const toggleTableView = () => {
    setAggregatedTable((prev) => !prev);
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-100">
        <p className="text-xl">Loading IP addresses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-100">
        <p className="text-xl text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">
            Visitor Log
          </h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleTableView}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
            >
              Toggle View
            </button>
            <input
              type="text"
              placeholder="Search by IP or Location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full max-w-sm p-2 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-300"
            />
          </div>
        </div>

        {/* Modern Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  #
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                  Location
                </th>
                {aggregatedTable && (
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                    Last Visit
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredData.map((ip, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {ip.ip}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {ip.location}
                  </td>
                  {aggregatedTable && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(ip.last_visited).toLocaleString()}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminVisitors;